import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { CheckCircle2, XCircle, Mail, Activity, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function HealthPill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`diag-${label.toLowerCase().replace(/\s/g,"-")}`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {ok ? "OK" : "Issue"}
    </span>
  );
}

export default function AdminDiagnosticsCard() {
  const [data, setData] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [resyncing, setResyncing] = useState(false);

  const load = async () => {
    try {
      const res = await apiClient.get("/admin/diagnostics");
      setData(res.data);
    } catch {
      setData(null);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const sendTest = async () => {
    setTesting(true);
    try {
      const body = testTo ? { to: testTo } : {};
      const res = await apiClient.post("/admin/test-email", body);
      if (res.data?.dispatched) {
        toast.success(`Test email sent to ${res.data.recipient}`);
      } else {
        toast.error(`Email not sent: ${res.data?.reason || "unknown"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setTesting(false);
    }
  };

  if (!data) {
    return (
      <Card className="border-slate-200" data-testid="admin-diagnostics-loading">
        <CardContent className="p-4 text-sm text-slate-500">Loading diagnostics…</CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="admin-diagnostics-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-slate-500" />
            <h3 className="font-heading font-semibold text-slate-900">System Diagnostics</h3>
          </div>
          <span className="text-xs text-slate-400">{(data.generatedAt || "").slice(11, 19)} UTC</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div className="rounded-lg bg-slate-50 p-3" data-testid="diag-resend">
            <div className="text-[11px] uppercase text-slate-500 mb-1 flex items-center gap-1"><Mail className="h-3 w-3" /> Resend</div>
            <HealthPill ok={data.resend?.configured} label="Resend" />
            <div className="text-[10px] text-slate-500 mt-1 break-all">
              {data.resend?.configured
                ? (data.resend?.senderEmail || "no sender")
                : "Email not configured — add a Resend key"}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3" data-testid="diag-stripe">
            <div className="text-[11px] uppercase text-slate-500 mb-1">Stripe Webhook</div>
            <HealthPill ok={data.stripeWebhook?.healthy} label="Stripe" />
            <div className="text-[10px] text-slate-500 mt-1">
              {data.stripeWebhook?.lastProcessedAt
                ? `Last: ${(data.stripeWebhook.lastProcessedAt || "").slice(0, 16)}`
                : "Awaiting first webhook"}
            </div>
            {data.stripeWebhook?.lastEventType && (
              <div className="text-[10px] text-indigo-600 font-mono mt-0.5" data-testid="diag-stripe-event-type">
                {data.stripeWebhook.lastEventType}
              </div>
            )}
          </div>
          <div className="rounded-lg bg-slate-50 p-3" data-testid="diag-last-failed">
            <div className="text-[11px] uppercase text-slate-500 mb-1">Last Payment Sync</div>
            <div className="text-sm font-semibold text-slate-900">
              {data.lastFailedPaymentSync ? (data.lastFailedPaymentSync || "").slice(0, 16) : "Not synced yet"}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3" data-testid="diag-deliveries">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase text-slate-500">Deliveries</div>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-1.5 text-[10px]"
                onClick={async () => {
                  setResyncing(true);
                  try {
                    const res = await apiClient.post("/admin/resync-failed-deliveries");
                    toast.success(`Queued ${res.data?.queued || 0} deliveries for retry`);
                    await load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Resync failed");
                  } finally {
                    setResyncing(false);
                  }
                }}
                disabled={resyncing || (data.deliveries?.failed || 0) === 0}
                data-testid="diag-resync-btn"
                title="Resync all failed deliveries"
              >
                <RefreshCw className={`h-3 w-3 ${resyncing ? "animate-spin" : ""}`} />
              </Button>
            </div>
            <div className="text-sm font-semibold text-red-600">{data.deliveries?.failed || 0} failed</div>
            <div className="text-[10px] text-slate-500">{data.deliveries?.pending || 0} pending</div>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
          <Input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="Test email to… (blank = your account)"
            className="h-8 text-xs flex-1"
            data-testid="diag-test-email-input"
          />
          <Button
            size="sm"
            onClick={sendTest}
            disabled={testing || !data.resend?.configured}
            data-testid="diag-send-test-btn"
          >
            {testing ? "Sending…" : "Send test"}
          </Button>
        </div>
        {!data.resend?.configured && (
          <div className="mt-2 text-[11px] text-amber-600">Add RESEND_API_KEY in backend/.env to enable test sending.</div>
        )}
      </CardContent>
    </Card>
  );
}
